import { MedplumClient } from "@medplum/core";
import { Bundle, QuestionnaireResponse, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { isYesAnswer } from "@/components/ClearableYesNoRadioField";
import {
  FETAL_HEART_RATE_ASSESS_BASELINE_LINK_ID,
  FETAL_HEART_RATE_BASELINE_DURATION_OF_LISTENING_LINK_ID,
  FETAL_HEART_RATE_BASELINE_LINK_ID,
  FETAL_HEART_RATE_QUESTIONNAIRE_NAME,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/constants/fetal-heart-rate-link-ids";
import { readFhrClinicalDateTimeFromResponse } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/utils/fhr-clinical-datetime";
import { findQuestionnaireResponseItem } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";
import { getQuestionnaire } from "@/encounters/components/EncounterLabor/services/labor";
import { isLoincAffirmativeYesFromCoding } from "@/encounters/components/EncounterLabor/utils/loincAnswerCoding";
import { resolveFetalHeartRateRootGroup } from "@/encounters/components/EncounterLabor/utils/resolve-fetal-heart-rate-root-group";
import { longUSDateTimeFormat } from "@/utils/datetime";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

export const FETAL_HEART_RATE_QUESTIONNAIRE_LOAD_ERROR =
  "Fetal heart rate questionnaire could not be loaded.";

export interface LastAssessedBaselineFetalHeartRate {
  date: string;
  heartRate: number;
  duration: number;
}

const BASELINE_SEARCH_PAGE_SIZE = 100;

function isAssessBaselineYes(
  fetalHeartRateItems: QuestionnaireResponseItem[] | undefined,
): boolean {
  const assessBaselineItem = findQuestionnaireResponseItem(
    fetalHeartRateItems,
    FETAL_HEART_RATE_ASSESS_BASELINE_LINK_ID,
  );
  const answer = assessBaselineItem?.answer?.[0];
  return isYesAnswer(answer) || isLoincAffirmativeYesFromCoding(answer?.valueCoding);
}

export function parseLastAssessedBaselineFetalHeartRateFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse,
): LastAssessedBaselineFetalHeartRate | undefined {
  const fetalHeartRateGroup = resolveFetalHeartRateRootGroup(questionnaireResponse.item);
  if (!fetalHeartRateGroup?.item || !isAssessBaselineYes(fetalHeartRateGroup.item)) {
    return undefined;
  }

  const { effectiveDateTime } = readFhrClinicalDateTimeFromResponse(questionnaireResponse);

  const heartRate = findQuestionnaireResponseItem(
    fetalHeartRateGroup.item,
    FETAL_HEART_RATE_BASELINE_LINK_ID,
  )?.answer?.[0]?.valueDecimal;
  const duration = findQuestionnaireResponseItem(
    fetalHeartRateGroup.item,
    FETAL_HEART_RATE_BASELINE_DURATION_OF_LISTENING_LINK_ID,
  )?.answer?.[0]?.valueDecimal;

  if (heartRate === undefined || heartRate === null) {
    return undefined;
  }

  return {
    date: effectiveDateTime ? longUSDateTimeFormat(effectiveDateTime) : "-",
    heartRate,
    duration: duration ?? 0,
  };
}

/**
 * Normalizes `medplum.searchResources` results (resource array or `{ bundle }`) to
 * QuestionnaireResponse resources.
 */
function getQuestionnaireResponsesFromSearchResult(searchResult: unknown): QuestionnaireResponse[] {
  if (Array.isArray(searchResult)) {
    return searchResult.filter(
      (resource): resource is QuestionnaireResponse =>
        resource?.resourceType === "QuestionnaireResponse",
    );
  }

  if (searchResult && typeof searchResult === "object" && "bundle" in searchResult) {
    return getResourcesFromBundle<QuestionnaireResponse>(
      "QuestionnaireResponse",
      (searchResult as { bundle?: Bundle<QuestionnaireResponse> }).bundle,
    );
  }

  return [];
}

export async function fetchLastAssessedBaselineFetalHeartRate(
  medplum: MedplumClient,
  encounterId: string,
): Promise<LastAssessedBaselineFetalHeartRate | undefined> {
  const questionnaire = await getQuestionnaire(medplum, FETAL_HEART_RATE_QUESTIONNAIRE_NAME);
  if (!questionnaire?.id) {
    throw new Error(FETAL_HEART_RATE_QUESTIONNAIRE_LOAD_ERROR);
  }

  let offset = 0;
  let bestMatch: { baseline: LastAssessedBaselineFetalHeartRate; effectiveAt: number } | undefined;
  let isLastPage = false;

  do {
    const searchResult = await medplum.searchResources("QuestionnaireResponse", {
      encounter: joinReferenceString("Encounter", encounterId),
      questionnaire: joinReferenceString("Questionnaire", questionnaire.id),
      _count: String(BASELINE_SEARCH_PAGE_SIZE),
      _offset: String(offset),
      _sort: "-_lastUpdated",
    });

    const responses = getQuestionnaireResponsesFromSearchResult(searchResult);

    for (const response of responses) {
      const baseline = parseLastAssessedBaselineFetalHeartRateFromQuestionnaireResponse(response);
      if (!baseline) {
        continue;
      }

      const effectiveAt = readFhrClinicalDateTimeFromResponse(response).effectiveAtMs;
      if (effectiveAt === undefined) {
        continue;
      }

      if (!bestMatch || effectiveAt > bestMatch.effectiveAt) {
        bestMatch = { baseline, effectiveAt };
      }
    }

    isLastPage = responses.length < BASELINE_SEARCH_PAGE_SIZE;
    if (!isLastPage) {
      offset += BASELINE_SEARCH_PAGE_SIZE;
    }
  } while (!isLastPage);

  return bestMatch?.baseline;
}
